import { Loader2 } from 'lucide-react';
import { useId, useState, type ComponentProps, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { Input } from './input.js';

/** Le fournisseur des suggestions, dont l'attribution est due sous chaque champ qui les affiche. */
export const GEOAPIFY_URL = 'https://www.geoapify.com/';

/**
 * - `idle` : rien à montrer — saisie trop courte, ou suggestion déjà choisie ;
 * - `loading` : une requête court, seul le champ tourne ;
 * - `ready` : la réponse est là, la liste ou « aucune adresse ».
 */
export type AddressAutocompleteStatus = 'idle' | 'loading' | 'ready';

export interface AddressAutocompleteProps<T> extends Omit<ComponentProps<'input'>, 'onSelect'> {
  items: readonly T[];
  itemKey: (item: T) => string;
  itemLabel: (item: T) => string;
  onSelectItem: (item: T) => void;
  status: AddressAutocompleteStatus;
  emptyLabel: string;
  listLabel: string;
  /** Le libellé du lien d'attribution, traduit par l'appelant ; le lien lui-même vit ici. */
  poweredByLabel: string;
  /** Icône posée à gauche du champ, comme l'épingle du dossier patient. */
  icon?: ReactNode;
}

/**
 * Champ d'adresse à suggestions : combobox et listbox, flèches pour parcourir, Entrée pour choisir,
 * Échap pour refermer. Il ne sait rien du réseau : l'appelant fournit les suggestions et leur état.
 * Le lien « Powered by Geoapify » est rendu ici, sous le champ, pour qu'aucun écran ne l'oublie.
 */
export function AddressAutocomplete<T>({
  items,
  itemKey,
  itemLabel,
  onSelectItem,
  status,
  emptyLabel,
  listLabel,
  poweredByLabel,
  icon,
  className,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  ...inputProps
}: AddressAutocompleteProps<T>) {
  const [open, setOpen] = useState(false);
  // L'option désignée au clavier ne vaut que pour la liste qui l'a produite.
  const [cursor, setCursor] = useState<{ items: readonly T[]; index: number }>({
    items,
    index: -1,
  });
  const listId = useId();
  const active = cursor.items === items ? cursor.index : -1;
  const showPanel = open && status === 'ready';
  const showList = showPanel && items.length > 0;
  const optionId = (index: number) => `${listId}-${String(index)}`;

  const choose = (item: T) => {
    setOpen(false);
    onSelectItem(item);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.key === 'Escape' && showPanel) {
      event.preventDefault();
      setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' && !open) {
      event.preventDefault();
      setOpen(true);
      return;
    }
    if (!showList) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const start = active === -1 ? (step === 1 ? -1 : 0) : active;
      setCursor({ items, index: (start + step + items.length) % items.length });
    } else if (event.key === 'Enter' && active >= 0) {
      event.preventDefault();
      const item = items[active];
      if (item !== undefined) choose(item);
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        {icon}
        <Input
          {...inputProps}
          role="combobox"
          aria-expanded={showList}
          aria-controls={showList ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={showList && active >= 0 ? optionId(active) : undefined}
          autoComplete="off"
          className={cn(status === 'loading' && 'pr-9', className)}
          onChange={(event) => {
            setOpen(true);
            onChange?.(event);
          }}
          onFocus={(event) => {
            setOpen(true);
            onFocus?.(event);
          }}
          onBlur={(event) => {
            setOpen(false);
            onBlur?.(event);
          }}
          onKeyDown={handleKeyDown}
        />
        {status === 'loading' && (
          <Loader2
            className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-muted-foreground"
            aria-hidden
            data-testid="state-address-loading"
          />
        )}
      </div>

      {showPanel && (
        <div
          className="absolute top-full z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-md border bg-popover shadow-lg"
          data-testid="address-suggestions"
        >
          {showList ? (
            <ul id={listId} role="listbox" aria-label={listLabel}>
              {items.map((item, index) => (
                <li
                  key={`${String(index)}-${itemKey(item)}`}
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === active}
                  className={cn(
                    'toggle-elevate cursor-pointer px-3 py-2 text-sm hover-elevate active-elevate-2',
                    index === active && 'toggle-elevated',
                  )}
                  // Avant le `blur` du champ, qui refermerait la liste sous le pointeur.
                  onMouseDown={(event) => {
                    event.preventDefault();
                  }}
                  onClick={() => {
                    choose(item);
                  }}
                  data-testid={`address-suggestion-${String(index)}`}
                >
                  {itemLabel(item)}
                </li>
              ))}
            </ul>
          ) : (
            <p
              role="status"
              className="p-3 text-center text-sm text-muted-foreground"
              data-testid="state-address-empty"
            >
              {emptyLabel}
            </p>
          )}
        </div>
      )}

      <a
        href={GEOAPIFY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-1 inline-block text-xs text-muted-foreground hover:underline"
        data-testid="link-geoapify"
      >
        {poweredByLabel}
      </a>
    </div>
  );
}
